<template>
  <form class="af-card" @submit.prevent="doSubmit" autocomplete="new-password">
    <validation-observer ref="observer">
      <template v-if="page === 0">
        <p class="is-size-3 has-text-weight-bold">
          {{ $t("af:forgotten_password.title") }}
        </p>

        <p class="info has-text-grey-icon">
          {{ $t("af:forgotten_password.subtitle") }}
        </p>

        <ValidationProvider rules="required" v-slot="{ errors, valid }">
          <b-field
            :label="$t('af:forgotten_password.input.phone_or_email.label')"
            label-position="on-border"
            class="mb-5"
            :type="{ 'is-danger': errors[0], 'is-success': valid }"
            :message="errors"
          >
            <b-input v-model="user"></b-input>
          </b-field>
        </ValidationProvider>

        <b-field>
          <b-button
            type="is-blue"
            native-type="submit"
            :loading="reqLoading"
            expanded
            >{{ $t("af:forgotten_password.button.next") }}
          </b-button>
        </b-field>
      </template>

      <template v-else-if="page === 1">
        <template v-if="looksLikeAnEmail">
          <img
            class="mx-auto is-block"
            :src="require('@/assets/images/mail-sent.svg')"
            alt=""
          />

          <p class="is-size-3 has-text-weight-bold">
            {{ $t("af:forgotten_password.email.title") }}
          </p>

          <p class="info has-text-grey-icon">
            {{ $t("af:forgotten_password.email.subtitle") }}
          </p>
        </template>

        <template v-else>
          <img
            class="mx-auto is-block mb-4"
            :src="require('@/assets/images/two-factor-phone.svg')"
            alt=""
          />

          <p class="is-size-3 has-text-weight-bold">
            {{ $t("af:forgotten_password.phone.title") }}
          </p>

          <p class="info has-text-grey-icon" v-if="this.forcePasswordChange">
            {{ $t("af:forgotten_password.password_change_required.title") }}
          </p>
        </template>

        <BInputWithValidation
          :label="$t('af:forgotten_password.input.security_code.label')"
          rules="required"
          label-position="on-border"
          autocomplete="new-password"
          :name="name"
          v-model="code"
        />

        <!--<BInputWithValidation-->
        <!--  :label="$t('af:forgotten_password.input.new_password.label')"-->
        <!--  rules="required"-->
        <!--  label-position="on-border"-->
        <!--  v-model="newPassword"-->
        <!--/>-->

        <b-field
          :label="$t('af:forgotten_password.input.new_password.label')"
          rules="required"
          label-position="on-border"
        >
          <PasswordCheckerInput
            v-model="newPassword"
            @validated="passwordValid = $event"
          />
        </b-field>

        <b-field>
          <b-button
            type="is-blue"
            native-type="submit"
            expanded
            :loading="finishLoading"
            >{{ $t("af:forgotten_password.button.submit") }}
          </b-button>
        </b-field>
      </template>
    </validation-observer>
  </form>
</template>

<script>
import i18n from "@/plugins/i18n";
import { mapGetters } from "vuex";
import { generateNonce } from "@/utils/util";
import Vue from "vue";
import {
  clearLandingParams,
  getActiveLandingParams,
} from "../utils/landingParamUtil";
import twoFactorMixin from "../mixins/twoFactorMixin";
import forgotPasswordMixin from "../mixins/forgotPasswordMixin";

export default {
  name: "ForgotPassword",
  title: "af:page.forgot_password.title",
  components: {
    BInputWithValidation: () => import("@/components/BInputWithValidation.vue"),
    PasswordCheckerInput: () => import("@/components/PasswordCheckerInput.vue"),
  },
  mixins: [twoFactorMixin, forgotPasswordMixin],
  data() {
    return {
      user: "",
      nonce: generateNonce(),
      code: "",
      chId: "",
      newPassword: "",
      passwordValid: false,
      page: 0, // 0: request, 1: solution
      reqLoading: false,
      finishLoading: false,
      forcePasswordChange: false,
      name: (Math.random() + 1).toString(36).substring(5),
    };
  },
  mounted() {
    if (this.loggedIn) {
      this.$router.push("/user/profile");
    }
  },
  computed: {
    ...mapGetters({
      loggedIn: "session/isReady",
      secondFactor: "session/secondFactor",
    }),
    looksLikeAnEmail() {
      return this.user.includes("@");
    },
  },
  created() {
    // parse URL from the verification e-mail
    const code = this.$route.query.code;
    if (code) {
      this.code = code;
      this.page = 1;
    }
    // Required password change
    if (this.secondFactor) {
      this.page = 1;
      this.chId = this.secondFactor.id;
      this.nonce = this.secondFactor.nonce;
      this.forcePasswordChange = true;
      this.$store.dispatch("session/deleteSecondFactor");
    }
  },
  methods: {
    async doSubmit() {
      const valid = await this.$refs.observer.validate();
      if (!valid) return;

      const recaptchaToken = await this.executeRecaptcha();

      Vue.set(this, "user", this.addPhonePadding(this.user));

      if (this.page === 0) {
        try {
          this.reqLoading = true;
          let reqResponse = await this.$store.dispatch(
            "session/requestPwdChange",
            {
              user: this.user,
              nonce: this.nonce,
              recaptchaToken,
            }
          );
          this.chId = reqResponse.id;
          this.code = reqResponse.code; // TODO REMOVE IN PROD !!!

          this.reqLoading = false;
          this.page = 1;
        } catch (ex) {
          console.error(ex);
          this.$buefy.toast.open({
            message: i18n.t("af:forget.error.general"),
            type: "is-danger",
          });
          this.reqLoading = false;
        }
      } else {
        if (!this.passwordValid) return;

        try {
          this.finishLoading = true;

          let redirect = await this.finishPasswordChange({
            id: this.chId,
            nonce: this.nonce,
            code: this.code,
            newPassword: this.newPassword,
            landingParams: getActiveLandingParams(),
          });

          clearLandingParams();
          this.$router.push(redirect);
          this.finishLoading = false;
        } catch (ex) {
          console.error(ex);
          this.finishLoading = false;
          if (ex.response.status === 400) {
            this.$buefy.toast.open({
              message: i18n.t("af:forget.new_password_cannot_be_old_password"),
              type: "is-danger",
            });
          } else {
            this.$buefy.toast.open({
              message: i18n.t("af:forget.error.general"),
              type: "is-danger",
            });
          }
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
.af-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
}

.info {
  margin-top: -1rem;
  margin-bottom: 2rem !important;
  max-width: 75%;
}

@media only screen and (min-width: 769px) {
  .af-card {
    padding: 32px 36px;
  }
}
</style>
